// apps/client/src/title/backdrop.ts (title screen, 2026-10-05) — when the title's 3D town (title/stage.ts) lives: from launch, behind the
// title, sign-in, the creator and the towns list (and the invite landing and found-a-town), until the player enters a real town. Then it
// hands over: the stage frees what it made and the Game takes over the page's renderer. It is not built again in the same launch, so the
// towns list after a town visit is the plain cream page, with the visited town's island still kept for the next entry (two islands at
// once would double the GPU memory on a phone).
import { tuning } from '@whiskerton/content';
import { pageAssets } from '../assets/shared';
import { IS_E2E } from '../boot/env';
import type { QualityLevel } from '../engine/quality';
import { pageRenderer, peekPageRenderer } from '../engine/pageRenderer';
import type { Phase } from '../state/slices/core';
import { useGame } from '../state/store';
import { stageDeps, TitleStage, type BackdropStatus } from './stage';

type Stage = Pick<TitleStage, 'start' | 'dispose' | 'status' | 'frames' | 'sceneCalls'>;
let stage: Stage | null = null;
let handedOver = false;
/** The page renderer's live geometries just before and just after the stage was disposed (the town has drawn nothing yet then). */
let handover: { before: number; after: number } | null = null;
const rendererGeometries = (): number | null => peekPageRenderer()?.stats().geometries ?? null;

const inTownOrOnTheWay = (p: Phase) => p === 'connecting' || p === 'inTown';
/** The title draws at low quality unless the player chose high in Settings: it has to come up fast on a phone. */
export function titleQuality(): QualityLevel { return useGame.getState().settings.quality === 'high' ? 'high' : 'low'; }
/** E2E builds draw the backdrop only in a browser context that asks for it (e2e/helpers/title.ts `withBackdrop`): every Playwright context
 *  shares one SwiftShader GPU process, so a second player's title, building its town while the first player's town is drawing, stalled
 *  that page's main thread for tens of seconds. The title, its music and its routing are the same either way; v1-title.spec.ts turns the
 *  backdrop on and checks it, its budget and its handover. */
export const E2E_BACKDROP_KEY = 'whiskerton-e2e-backdrop';
function wanted(): boolean {
  if (!IS_E2E) return true;
  try { return localStorage.getItem(E2E_BACKDROP_KEY) === '1'; } catch { return false; }
}
const defaults = {
  make: (): Stage => new TitleStage(stageDeps(() => pageRenderer(titleQuality()), pageAssets)),
  /** After the first paint, so the title's words and Play now show before any 3D work starts. */
  later: (fn: () => void) => { requestAnimationFrame(() => setTimeout(fn, 0)); },
  wanted,
  geometries: rendererGeometries,
};

/** main.tsx: starts the backdrop for this launch and hands it over on the first town entry. Returns the uninstall (tests). */
export function installBackdrop(opts: Partial<typeof defaults> = {}): () => void {
  const o = { ...defaults, ...opts };
  if (handedOver || !o.wanted() || inTownOrOnTheWay(useGame.getState().phase)) return () => {};
  const handOver = () => {
    handedOver = true;
    const before = o.geometries();
    stage?.dispose();
    const after = o.geometries();
    if (stage && before !== null && after !== null) handover = { before, after };
    unsubscribe();
  };
  const unsubscribe = useGame.subscribe((s) => { if (inTownOrOnTheWay(s.phase)) handOver(); });
  o.later(() => {
    if (handedOver) return;
    stage = o.make();
    void stage.start();
  });
  return () => { unsubscribe(); stage?.dispose(); stage = null; handedOver = false; handover = null; };
}

/** `maxDrawCalls`: the scene draw-call budget it must keep to (tuning.render, spec §8.7). `geometries`: the page renderer's live
 *  geometries now; `handover`: the same count just before and just after the stage was freed on the first town entry (null before). */
export interface BackdropInfo { status: BackdropStatus | 'none'; frames: number; sceneCalls: number; maxDrawCalls: number; geometries: number | null;
  handover: { before: number; after: number } | null }
/** For the test hooks (boot/launchHooks.ts). */
export function backdropInfo(): BackdropInfo {
  return { status: stage?.status ?? 'none', frames: stage?.frames ?? 0, sceneCalls: stage?.sceneCalls() ?? 0, maxDrawCalls: tuning.render.maxDrawCalls,
    geometries: rendererGeometries(), handover };
}
